import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useUserRatings } from "./UserRatingsContext";
import { CHALLENGES } from "../utility/challenges";
import { useFirebase } from "./FirebaseContext";
import { readIndexedDbValue } from "../utility/indexedDbCache";
import { useActivityLogger } from "../hooks/useActivityLogger";
import {
  deduplicateRatingsByMediaId,
  isGameProgressEligible
} from "../utility/gameProgressEligibility";
import { isDemoUserId, markDemoActivity } from "../utility/demoSession";
import { requestExperienceSettlement } from "../utility/experienceSettlementApi";
import type { PersonCredit } from "../types/media";
import type { Rating, RatingHistoryEntry } from "../types/rating";
import { isUnknownArray, dateValueToMillis, isRecord } from "../types/data";

const DAY = 24 * 60 * 60 * 1000;
const MAX_ACTIVE_CHALLENGES = 3;
const PEOPLE_CREDITS_CACHE_KEY = "people-statistics-credits-v4";
const CHALLENGE_LOAD_RETRY_DELAY_MS = 1500;
const CHALLENGE_SAVE_RETRY_DELAY_MS = 1500;
const CHALLENGE_EXPERIENCE_RECOVERY_VERSION = 2;

export type ChallengeStatus = "available" | "active" | "completed" | "expired";

interface ChallengeInstance {
  id: string;
  startedAt: number;
  status: Exclude<ChallengeStatus, "available">;
  completedAt?: number;
}

export type ChallengeView = (typeof CHALLENGES)[number] & {
  status: ChallengeStatus;
  progress: number;
  startedAt?: number;
  completedAt?: number;
  deadline?: number;
  remainingMs?: number;
};

export type ChallengeStartResult =
  | { ok: true }
  | { ok: false; message: string; reason?: string };

export interface ChallengesContextValue {
  challenges: ChallengeView[];
  activeChallenges: ChallengeView[];
  completedChallenges: ChallengeView[];
  startChallenge: (id: string) => ChallengeStartResult;
  abandonChallenge: (id: string) => void;
  canManage: boolean;
  maxActive: number;
  loaded: boolean;
  syncing: boolean;
}

const ChallengesContext = createContext<ChallengesContextValue | null>(null);

const getRatingTime = (rating: Rating): number => {
  const value = rating?.firstTrackedAt || rating?.firstRatedAt || rating?.ratedAt;
  if (!value) return 0;
  return dateValueToMillis(value);
};

const getRatingUpdatedTime = (rating: Rating): number => {
  const value =
    rating?.updatedAt ||
    rating?.lastRatedAt ||
    rating?.deletedAt ||
    rating?.ratedAt ||
    rating?.firstTrackedAt;
  if (!value) return 0;
  return dateValueToMillis(value);
};

interface LocalRatingRemoval { removedAt: number; ratingLifecycleId: string; }

const isRatingHiddenByLocalRemoval = (rating: Rating, removal?: LocalRatingRemoval): boolean => {
  if (!removal) return false;
  const removedLifecycleId = String(removal.ratingLifecycleId || "");
  const ratingLifecycleId = String(rating?.ratingLifecycleId || "");

  // Lifecycle ids are stronger than clocks. A tombstone must keep its exact
  // deleted lifecycle hidden, but it must never hide a later re-rating merely
  // because a cached/server timestamp arrived in a different order.
  if (removedLifecycleId && ratingLifecycleId) {
    return removedLifecycleId === ratingLifecycleId;
  }

  // Legacy records can miss either id. Keep the original timestamp guard for
  // those only: it protects against a stale active cache immediately after a
  // delete, while allowing an unquestionably newer active value to recover.
  const removedAt = Number(removal.removedAt) || 0;
  const ratingUpdatedAt = getRatingUpdatedTime(rating);
  return !ratingUpdatedAt || Boolean(removedAt && ratingUpdatedAt <= removedAt);
};

const isChallengeProgressEligible = (rating: Rating) =>
  rating?.active !== false && isGameProgressEligible(rating);

const getMediaType = (rating: Rating): "tv" | "movie" =>
  rating?.mediaType === "tv" || rating?.media_type === "tv" || rating?.first_air_date ||
  (isUnknownArray(rating?.seasons) && rating.seasons.length > 0) ? "tv" : "movie";
const mergePeople = (first: PersonCredit[] = [], second: PersonCredit[] = []): PersonCredit[] => {
  const people = new Map<string, PersonCredit>();
  [...first, ...second].forEach((person) => {
    const key = person?.id != null ? String(person.id) : String(person?.name || "").toLowerCase();
    if (key) people.set(key, { ...(people.get(key) || {}), ...person });
  });
  return [...people.values()];
};

const normalizeChallengeInstances = (value: unknown): ChallengeInstance[] => {
  if (!isUnknownArray(value)) return [];
  return value.flatMap((item: unknown): ChallengeInstance[] => {
    if (!item || typeof item !== "object" || !("id" in item) || typeof item.id !== "string" || !item.id) return [];
    const startedAt = "startedAt" in item ? Number(item.startedAt) : 0;
    const completedAt = "completedAt" in item ? Number(item.completedAt) : 0;
    const status = "status" in item && (item.status === "completed" || item.status === "expired")
      ? item.status
      : "active";
    return [{ id: item.id, startedAt: startedAt || Date.now(), status, ...(completedAt ? { completedAt } : {}) }];
  });
};

const getStoredTime = (value: unknown): number => {
  if (!value) return 0;
  return dateValueToMillis(value);
};

const reconcileLegacyChallengeInstances = (
  remoteValue: unknown,
  legacyValue: unknown,
  remoteUpdatedAt: unknown,
  remoteDocumentExists = false
) => {
  const remoteInstances = normalizeChallengeInstances(remoteValue);
  const legacyInstances = normalizeChallengeInstances(legacyValue);
  // An existing empty cloud document is an intentional, authoritative state.
  // Only migrate localStorage when the cloud document itself does not exist;
  // otherwise an old browser could resurrect every abandoned challenge.
  if (!remoteInstances.length) {
    return remoteDocumentExists ? remoteInstances : legacyInstances;
  }
  if (!legacyInstances.length) return remoteInstances;

  const remoteUpdatedTime = getStoredTime(remoteUpdatedAt);
  const mergedById = new Map(remoteInstances.map((item) => [String(item.id), item]));

  legacyInstances.forEach((item) => {
    const id = String(item.id);
    if (mergedById.has(id)) return;
    // localStorage is only a recovery journal for a write that did not reach
    // Firestore. An older extra item may be a challenge deliberately removed
    // on another device, so merely containing every remote id must never make
    // that stale item authoritative again.
    const wasAddedAfterRemoteSave = remoteUpdatedTime > 0 && Number(item.startedAt) > remoteUpdatedTime;
    if (wasAddedAfterRemoteSave) {
      mergedById.set(id, item);
    }
  });

  const merged = [...mergedById.values()];
  const active = merged
    .filter((item) => item.status === "active")
    .sort((first, second) => Number(first.startedAt) - Number(second.startedAt));
  if (active.length <= MAX_ACTIVE_CHALLENGES) return merged;

  const allowedActiveIds = new Set(
    active.slice(-MAX_ACTIVE_CHALLENGES).map((item) => String(item.id))
  );
  return merged.filter(
    (item) => item.status !== "active" || allowedActiveIds.has(String(item.id))
  );
};

const getChallengeInstancesSignature = (value: ChallengeInstance[] | null | undefined): string =>
  JSON.stringify(
    (isUnknownArray(value) ? value : [])
      .filter((item) => item?.id)
      .map((item) => ({
        id: String(item.id),
        startedAt: Number(item.startedAt) || 0,
        status: item.status || "active",
        completedAt: Number(item.completedAt) || 0
      }))
      .sort((first, second) =>
        first.id.localeCompare(second.id) ||
        first.startedAt - second.startedAt ||
        first.status.localeCompare(second.status) ||
        first.completedAt - second.completedAt
      )
  );

export function ChallengesProvider({ children, userId }: { children?: React.ReactNode; userId?: string | null }) {
  const {
    ratings,
    ratingHistory,
    loading: ratingsLoading,
    historyLoading,
    initialSyncLoading,
    ratingsSyncReady
  } = useUserRatings();
  const { auth, firestore } = useFirebase();
  const { logChallengeCompleted, removeActivity } = useActivityLogger();
  const completedIdsRef = useRef<Set<string> | null>(null);
  const persistenceGenerationRef = useRef(0);
  const persistedInstancesSignatureRef = useRef<string | null>(null);
  const scheduledInstancesSignatureRef = useRef<string | null>(null);
  const persistenceQueueRef = useRef(Promise.resolve());
  const persistenceRetryTimerRef = useRef<number | null>(null);
  const experienceRecoveryAttemptRef = useRef("");
  const [instances, setInstances] = useState<ChallengeInstance[]>([]);
  const instancesRef = useRef(instances);
  instancesRef.current = instances;
  const [loaded, setLoaded] = useState(false);
  const [loadedStorageKey, setLoadedStorageKey] = useState<string | null>(null);
  const [loadRetryRevision, setLoadRetryRevision] = useState(0);
  const [persistenceRetryRevision, setPersistenceRetryRevision] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [creditsByFilm, setCreditsByFilm] = useState<Record<string, { cast?: PersonCredit[]; crew?: PersonCredit[] }>>({});
  const [locallyRemovedRatings, setLocallyRemovedRatings] = useState(() => new Map<string, LocalRatingRemoval>());
  const storageKey = `movie_challenges_v1_${userId}`;
  const canManage = Boolean(userId && auth.currentUser?.uid === userId);
  // Keep the demo branch stable while Firebase publishes the signed-out state.
  // Tying it to auth.currentUser made the still-mounted provider briefly issue
  // a real Firestore read during logout and report a permission error.
  const isDemoMode = isDemoUserId(userId);
  const ratingsReady =
    ratingsLoading !== true &&
    historyLoading !== true &&
    initialSyncLoading !== true &&
    ratingsSyncReady !== false;
  const storageLoaded = loaded && loadedStorageKey === storageKey;
  // The saved challenge selection is enough to render the page. Ratings are
  // only required before recalculating or persisting progress; waiting for the
  // entire ratings collection here made the first challenge card appear tens
  // of seconds late on a throttled connection.
  const displayLoaded = storageLoaded;
  const contextLoaded = storageLoaded && ratingsReady;

  useEffect(() => {
    setLocallyRemovedRatings(new Map<string, LocalRatingRemoval>());
    experienceRecoveryAttemptRef.current = "";
  }, [userId]);

  useEffect(() => {
    if (!storageLoaded || !canManage || !userId || isDemoMode || instances.length === 0) return;
    const recoveryKey =
      `movie_challenges_experience_recovery_v${CHALLENGE_EXPERIENCE_RECOVERY_VERSION}_${userId}`;
    try {
      if (localStorage.getItem(recoveryKey) === "done") return;
    } catch {
      // A blocked localStorage must not prevent the one-time server repair.
    }
    if (experienceRecoveryAttemptRef.current === recoveryKey) return;
    experienceRecoveryAttemptRef.current = recoveryKey;

    void requestExperienceSettlement({
      auth,
      firestore,
      userId,
      reason: `challenge-recovery-v${CHALLENGE_EXPERIENCE_RECOVERY_VERSION}`
    }).then((result) => {
      if (!result?.publicStats) {
        experienceRecoveryAttemptRef.current = "";
        return;
      }
      try {
        localStorage.setItem(recoveryKey, "done");
      } catch {
        // The in-memory guard still prevents a request loop in this session.
      }
    }).catch((error: unknown) => {
      experienceRecoveryAttemptRef.current = "";
      console.warn("Не удалось проверить ранее сохранённые награды испытаний:", error);
    });
  }, [auth, canManage, firestore, instances.length, isDemoMode, storageLoaded, userId]);

  useEffect(() => {
    if (typeof window === "undefined" || !userId) return undefined;

    const handleFilmUserStateChanged = (event: Event) => {
      if (!(event instanceof CustomEvent) || !isRecord(event.detail)) return;
      const detail = event.detail;
      if (String(detail.userId || "") !== String(userId)) return;
      const rating = isRecord(detail.rating) ? detail.rating : null;
      const filmId = detail.filmId ?? rating?.filmId ?? rating?.id;
      if (filmId == null) return;
      const key = String(filmId);

      setLocallyRemovedRatings((current) => {
        const next = new Map(current);
        if (detail.ratingRemoved) {
          next.set(key, {
            removedAt: Date.now(),
            ratingLifecycleId: String(detail.ratingLifecycleId || "")
          });
        }
        else if (detail.rating || detail.ratingCreated) next.delete(key);
        else return current;
        return next;
      });
    };

    window.addEventListener("movie-film-user-state-changed", handleFilmUserStateChanged);
    return () => {
      window.removeEventListener("movie-film-user-state-changed", handleFilmUserStateChanged);
    };
  }, [userId]);

  const challengeRatings = useMemo(() => {
    const historyByFilmId = new Map<string, RatingHistoryEntry>();
    (ratingHistory || []).forEach((entry) => {
      const filmId = entry?.filmId || entry?.id;
      if (filmId == null) return;
      historyByFilmId.set(String(filmId), entry);
    });

    return deduplicateRatingsByMediaId((ratings || []).map((entry): Rating | null => {
      const filmId = entry?.filmId || entry?.id;
      if (filmId == null || entry?.active === false) return null;
      const key = String(filmId);
      const locallyRemoved = locallyRemovedRatings.get(key);
      if (isRatingHiddenByLocalRemoval(entry, locallyRemoved)) {
        return null;
      }
      const history = historyByFilmId.get(key);
      const ratingLifecycleId = String(entry?.ratingLifecycleId || "");
      const historyLifecycleId = String(history?.ratingLifecycleId || "");
      const sameLifecycle =
        !ratingLifecycleId ||
        !historyLifecycleId ||
        ratingLifecycleId === historyLifecycleId;
      if (
        history?.active === false &&
        sameLifecycle &&
        getRatingUpdatedTime(history) > getRatingUpdatedTime(entry)
      ) {
        return null;
      }

      const effectiveHistory =
        history?.active === false || !sameLifecycle
          ? null
          : history;
      const firstTrackedAt =
        entry.firstTrackedAt ||
        effectiveHistory?.firstTrackedAt ||
        entry.firstRatedAt ||
        effectiveHistory?.firstRatedAt ||
        entry.ratedAt ||
        effectiveHistory?.ratedAt ||
        null;

      return {
        ...(effectiveHistory || {}),
        ...entry,
        filmId: key,
        firstTrackedAt,
        firstRatedAt: entry.firstRatedAt || effectiveHistory?.firstRatedAt || firstTrackedAt,
        ratedAt: entry.ratedAt || effectiveHistory?.ratedAt || firstTrackedAt,
        active: true
      };
    }).filter((entry): entry is Rating => Boolean(entry)));
  }, [locallyRemovedRatings, ratings, ratingHistory]);

  const ratingsSignature = useMemo(
    () => challengeRatings.map((item) => `${getMediaType(item)}:${item.filmId || item.id}:${getRatingTime(item)}`).sort().join("|"),
    [challengeRatings]
  );

  useEffect(() => { completedIdsRef.current = null; }, [userId]);

  useEffect(() => () => {
    if (persistenceRetryTimerRef.current !== null) {
      window.clearTimeout(persistenceRetryTimerRef.current);
      persistenceRetryTimerRef.current = null;
    }
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    const loadCredits = async () => {
      const stored = await readIndexedDbValue<{ films?: Record<string, { cast?: PersonCredit[]; crew?: PersonCredit[] }> }>(PEOPLE_CREDITS_CACHE_KEY).catch(() => null);
      if (!cancelled) setCreditsByFilm(stored?.films || {});
    };
    const refresh = () => { void loadCredits(); };
    void loadCredits();
    window.addEventListener("people-statistics-cache-updated", refresh);
    return () => {
      cancelled = true;
      window.removeEventListener("people-statistics-cache-updated", refresh);
    };
  }, [ratingsSignature]);

  const enrichedRatings = useMemo(() => challengeRatings.map((rating) => {
    const key = `${getMediaType(rating)}:${rating.filmId || rating.id}`;
    const cached = creditsByFilm[key];
    return cached ? {
      ...rating,
      cast: mergePeople(rating.cast, cached.cast),
      crew: mergePeople(rating.crew, cached.crew)
    } : rating;
  }), [challengeRatings, creditsByFilm]);

  useEffect(() => {
    let cancelled = false;
    let retryTimer: number|null|undefined = null;
    const persistenceGeneration = persistenceGenerationRef.current + 1;
    persistenceGenerationRef.current = persistenceGeneration;
    persistedInstancesSignatureRef.current = null;
    scheduledInstancesSignatureRef.current = null;
    persistenceQueueRef.current = Promise.resolve();
    setLoaded(false);
    setLoadedStorageKey(null);
    completedIdsRef.current = null;

    const legacyInstances = (() => {
      if (!userId) return [];
      try {
        const stored = localStorage.getItem(storageKey);
        return normalizeChallengeInstances(stored ? JSON.parse(stored) : []);
      } catch (error) {
        console.warn("Не удалось загрузить старое локальное состояние испытаний:", error);
        return [];
      }
    })();

    const loadRemoteInstances = async () => {
      if (!userId) {
        const signature = getChallengeInstancesSignature([]);
        persistedInstancesSignatureRef.current = signature;
        scheduledInstancesSignatureRef.current = signature;
        setInstances([]);
        setLoaded(true);
        setLoadedStorageKey(storageKey);
        return;
      }

      if (isDemoMode) {
        const signature = getChallengeInstancesSignature(legacyInstances);
        persistedInstancesSignatureRef.current = signature;
        scheduledInstancesSignatureRef.current = signature;
        setInstances(legacyInstances);
        setLoaded(true);
        setLoadedStorageKey(storageKey);
        return;
      }

      try {
        const docRef = firestore
          .collection("users")
          .doc(userId)
          .collection("challengeState")
          .doc("active");
        const snapshot = await docRef.get();
        if (cancelled) return;

        const remoteData = snapshot.exists ? snapshot.data() : {};
        const remoteInstances = snapshot.exists
          ? normalizeChallengeInstances(remoteData?.instances)
          : [];
        const nextInstances = canManage
          ? reconcileLegacyChallengeInstances(
              remoteInstances,
              legacyInstances,
              remoteData?.updatedAt,
              snapshot.exists
            )
          : remoteInstances;
        const remoteSignature = getChallengeInstancesSignature(remoteInstances);
        const signature = getChallengeInstancesSignature(nextInstances);

        if (canManage && legacyInstances.length > 0 && signature !== remoteSignature) {
          await docRef.set({ instances: nextInstances, updatedAt: new Date() }, { merge: true });
          if (cancelled) return;
        }

        persistedInstancesSignatureRef.current = signature;
        scheduledInstancesSignatureRef.current = signature;
        setInstances(nextInstances);
        completedIdsRef.current = new Set(
          nextInstances.filter((item) => item.status === "completed").map((item) => item.id)
        );

        setLoaded(true);
        setLoadedStorageKey(storageKey);

        if (canManage && legacyInstances.length > 0) localStorage.removeItem(storageKey);
      } catch (error) {
        console.warn("Не удалось загрузить испытания из Firebase:", error);
        if (!cancelled) {
          // A failed read is not an empty challenge history. Keep the provider
          // in its loading state and retry instead of exposing an all-zero list
          // that can be mistaken for (and later persisted as) real data.
          retryTimer = window.setTimeout(() => {
            setLoadRetryRevision((current) => current + 1);
          }, CHALLENGE_LOAD_RETRY_DELAY_MS);
        }
      }
    };

    void loadRemoteInstances();

    return () => {
      cancelled = true;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
    };
  }, [canManage, firestore, isDemoMode, loadRetryRevision, storageKey, userId]);

  useEffect(() => {
    if (!contextLoaded || !userId || isDemoMode) return undefined;

    const docRef = firestore
      .collection("users")
      .doc(userId)
      .collection("challengeState")
      .doc("active");
    if (typeof docRef.onSnapshot !== "function") return undefined;

    const unsubscribe = docRef.onSnapshot(
      (snapshot) => {
        // Local latency-compensation events are acknowledged by the regular
        // persistence flow. Waiting for the server snapshot keeps a failed
        // write from being mistaken for persisted state.
        if (snapshot.metadata?.hasPendingWrites) return;

        const remoteInstances = snapshot.exists
          ? normalizeChallengeInstances(snapshot.data()?.instances)
          : [];
        const remoteSignature = getChallengeInstancesSignature(remoteInstances);
        const localSignature = getChallengeInstancesSignature(instancesRef.current);
        const persistedSignature = persistedInstancesSignatureRef.current;
        const hasUnpersistedLocalChanges =
          persistedSignature !== null && localSignature !== persistedSignature;

        // Do not replace a user action that is currently being saved. The
        // listener will receive the resulting server snapshot immediately
        // after Firestore commits that write.
        if (hasUnpersistedLocalChanges && localSignature !== remoteSignature) return;

        persistedInstancesSignatureRef.current = remoteSignature;
        scheduledInstancesSignatureRef.current = remoteSignature;
        if (localSignature !== remoteSignature) setInstances(remoteInstances);
      },
      (error) => {
        console.warn("Не удалось синхронизировать испытания между устройствами:", error);
      }
    );

    return typeof unsubscribe === "function" ? unsubscribe : undefined;
  }, [contextLoaded, firestore, isDemoMode, userId]);

  useEffect(() => {
    if (!contextLoaded) return;
    if (!canManage || !userId) return;
    const normalizedInstances = normalizeChallengeInstances(instances);
    const signature = getChallengeInstancesSignature(normalizedInstances);
    if (signature === scheduledInstancesSignatureRef.current) return;

    // Keep an unsaved change recoverable across a reload. The key is removed
    // only after the exact same state has reached Firestore.
    try {
      localStorage.setItem(storageKey, JSON.stringify(normalizedInstances));
    } catch (error) {
      console.warn("Не удалось сохранить резервную копию испытаний:", error);
    }

    scheduledInstancesSignatureRef.current = signature;
    if (isDemoMode) {
      persistedInstancesSignatureRef.current = signature;
      markDemoActivity(userId);
      return;
    }
    const persistenceGeneration = persistenceGenerationRef.current;
    const docRef = firestore
      .collection("users")
      .doc(userId)
      .collection("challengeState")
      .doc("active");

    persistenceQueueRef.current = persistenceQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        if (persistenceGeneration !== persistenceGenerationRef.current) return;
        await docRef.set(
          { instances: normalizedInstances, updatedAt: new Date() },
          { merge: true }
        );
        if (persistenceGeneration === persistenceGenerationRef.current) {
          persistedInstancesSignatureRef.current = signature;
          if (persistenceRetryTimerRef.current !== null) {
            window.clearTimeout(persistenceRetryTimerRef.current);
            persistenceRetryTimerRef.current = null;
          }
          if (getChallengeInstancesSignature(instancesRef.current) === signature) {
            localStorage.removeItem(storageKey);
          }
        }
        try {
          // The server must observe both the start and every later state
          // transition. Waiting until the first rating anchored the trusted
          // start a few seconds after that rating, leaving an 8-film challenge
          // permanently at 7/8 in the authoritative XP calculation.
          await requestExperienceSettlement({
            auth,
            firestore,
            userId,
            reason: "challenge-state-saved"
          });
        } catch (error) {
          // The challenge document is already durable. A later source change
          // or the one-time recovery pass can safely retry reconciliation.
          console.warn("Испытание сохранено, но опыт пока не пересчитан:", error);
        }
      })
      .catch((error: unknown) => {
        if (
          persistenceGeneration === persistenceGenerationRef.current &&
          scheduledInstancesSignatureRef.current === signature
        ) {
          scheduledInstancesSignatureRef.current = persistedInstancesSignatureRef.current;
          if (persistenceRetryTimerRef.current !== null) {
            window.clearTimeout(persistenceRetryTimerRef.current);
          }
          persistenceRetryTimerRef.current = window.setTimeout(() => {
            persistenceRetryTimerRef.current = null;
            if (persistenceGeneration === persistenceGenerationRef.current) {
              setPersistenceRetryRevision((current) => current + 1);
            }
          }, CHALLENGE_SAVE_RETRY_DELAY_MS);
        }
        console.warn("Не удалось сохранить испытания в Firebase:", error);
      });
  }, [
    canManage,
    contextLoaded,
    auth,
    firestore,
    instances,
    isDemoMode,
    persistenceRetryRevision,
    storageKey,
    userId
  ]);

  useEffect(() => {
    if (!contextLoaded || !canManage) return;
    const active = instances.filter((item) => item.status === "active");
    if (active.length <= MAX_ACTIVE_CHALLENGES) return;
    setInstances((current) => {
      const activeItems = current.filter((item) => item.status === "active");
      const allowedActiveIds = new Set(
        activeItems
          .slice()
          .sort((a, b) => Number(a.startedAt) - Number(b.startedAt))
          .slice(0, MAX_ACTIVE_CHALLENGES)
          .map((item) => item.id)
      );
      return current.filter((item) => item.status !== "active" || allowedActiveIds.has(item.id));
    });
  }, [canManage, contextLoaded, instances]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  const challenges = useMemo<ChallengeView[]>(
    () =>
      CHALLENGES.map((definition) => {
        const instance = instances.find((item) => item.id === definition.id);
        if (!instance) return { ...definition, status: "available", progress: 0 };

        const startedAt = Number(instance.startedAt);
        const deadline = startedAt + definition.durationDays * DAY;
        const eligibleRatings = enrichedRatings.filter((rating) => {
          if (!isChallengeProgressEligible(rating)) return false;
          const ratedAt = getRatingTime(rating);
          // The deadline is exclusive: an event at the exact boundary already
          // belongs to the period after the challenge.
          return ratedAt >= startedAt && ratedAt < deadline;
        });
        const calculatedProgress = Math.min(
          definition.target,
          definition.getProgress(eligibleRatings)
        );
        // Once all rating sources are hydrated, the current active ratings are
        // authoritative. A deleted rating must therefore revoke both the
        // challenge progress and its reward instead of leaving a completed
        // badge whose XP has already disappeared from the ledger.
        const progress = calculatedProgress;
        const computedStatus =
          progress >= definition.target
            ? "completed"
            : now >= deadline
            ? "expired"
            : "active";

        return {
          ...definition,
          ...instance,
          progress,
          deadline,
          status: computedStatus,
          remainingMs: Math.max(0, deadline - now)
        };
      }),
    [instances, now, enrichedRatings]
  );

  // While ratings are reconciling with Firestore, the persisted challenge
  // instance is the safest source for its status. Cached ratings may still be
  // incomplete and must not visually reopen a completed challenge (or mark an
  // active one as completed) before the authoritative sync finishes.
  const synchronizingChallenges = useMemo<ChallengeView[]>(
    () => CHALLENGES.map((definition) => {
      const instance = instances.find((item) => item.id === definition.id);
      if (!instance) return { ...definition, status: "available", progress: 0 };

      const calculated = challenges.find((item) => item.id === definition.id);
      return {
        ...definition,
        ...instance,
        progress: instance.status === "completed"
          ? definition.target
          : calculated?.progress || 0,
        deadline: calculated?.deadline,
        remainingMs: calculated?.remainingMs,
        status: instance.status || "active"
      };
    }),
    [challenges, instances]
  );

  useEffect(() => {
    if (!contextLoaded || !canManage) return;
    setInstances((current) => {
      let changed = false;
      const next = current.map((instance) => {
        const challenge = challenges.find((item) => item.id === instance.id);
        if (!challenge || challenge.status === "available" || challenge.status === instance.status) return instance;
        changed = true;
        if (challenge.status === "completed") {
          return {
            ...instance,
            status: challenge.status,
            completedAt: instance.completedAt || Date.now()
          };
        }

        const { completedAt: _completedAt, ...rest } = instance;
        return { ...rest, status: challenge.status };
      });
      return changed ? next : current;
    });
  }, [canManage, challenges, contextLoaded]);

  const exposedChallenges: ChallengeView[] = displayLoaded
    ? (contextLoaded ? challenges : synchronizingChallenges)
    : CHALLENGES.map((definition) => ({ ...definition, status: "available", progress: 0 }));
  const activeChallenges = displayLoaded
    ? exposedChallenges.filter((item) => item.status === "active")
    : [];

  useEffect(() => {
    if (!contextLoaded || !canManage) return;
    const completed = challenges.filter((item) => item.status === "completed");
    const ids = new Set(completed.map((item) => item.id));
    if (completedIdsRef.current === null) {
      completedIdsRef.current = ids;
      return;
    }
    const previousCompletedIds = completedIdsRef.current;
    previousCompletedIds.forEach((id) => {
      if (!ids.has(id)) void removeActivity(`challenge:${id}`);
    });
    completed.forEach((challenge) => {
      if (!previousCompletedIds.has(challenge.id)) void logChallengeCompleted(challenge);
    });
    completedIdsRef.current = ids;
  }, [canManage, challenges, contextLoaded, logChallengeCompleted, removeActivity]);

  const startChallenge = (id: string): ChallengeStartResult => {
    if (!contextLoaded) return { ok: false, message: "Испытания еще загружаются" };
    if (!canManage) return { ok: false, message: "Испытания можно менять только в своем профиле" };
    if (activeChallenges.length >= MAX_ACTIVE_CHALLENGES) {
      return {
        ok: false,
        reason: "max_active_challenges",
        message: `Одновременно можно выполнять только ${MAX_ACTIVE_CHALLENGES} испытаний`
      };
    }
    const existing = challenges.find((item) => item.id === id);
    if (!existing || existing.status === "active") return { ok: false, message: "Испытание уже активно" };
    if (existing.status === "completed") return { ok: false, message: "Испытание уже завершено" };

    setInstances((current) => [
      ...current.filter((item) => item.id !== id),
      { id, startedAt: Date.now(), status: "active" }
    ]);
    return { ok: true };
  };

  const abandonChallenge = (id: string): void => {
    if (!contextLoaded) return;
    if (!canManage) return;
    setInstances((current) => current.filter((item) => item.id !== id));
  };

  const value = {
    challenges: exposedChallenges,
    activeChallenges,
    completedChallenges: displayLoaded
      ? exposedChallenges.filter((item) => item.status === "completed")
      : [],
    startChallenge,
    abandonChallenge,
    canManage: canManage && contextLoaded,
    maxActive: MAX_ACTIVE_CHALLENGES,
    loaded: displayLoaded,
    syncing: displayLoaded && !contextLoaded
  };

  return <ChallengesContext.Provider value={value}>{children}</ChallengesContext.Provider>;
}

export const useChallenges = () => {
  const context = useContext(ChallengesContext);
  if (!context) throw new Error("useChallenges must be used inside ChallengesProvider");
  return context;
};
